import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowRightLeft, Info, TriangleAlert } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  declararFrontera,
  enrutarCuenta,
  fetchContraste,
  fetchPnl,
  fetchPuente,
  fetchRuteo,
  quitarFrontera,
  quitarPuente,
  tenderPuente,
} from "@/routes/finance/api"
import { useFinanceFilters } from "@/routes/finance/context"
import type {
  CuentaRuteo,
  Ensamblado,
  FuenteFinanzas,
  LineaDelPuente,
} from "@/routes/finance/mapping"
import { nombreCuenta, nombrePais } from "@/routes/finance/mapping"

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
               "agosto", "septiembre", "octubre", "noviembre", "diciembre"]

/** Un motivo corto no explica nada seis meses después. Lo mismo exige el backend. */
const MOTIVO_MINIMO = 10

const selectClase = cn(
  "h-7 rounded-md border bg-surface px-2 text-xs",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
)

/** Todo lo que lee esta pantalla cambia el P&L: se invalida junto. */
function useRefrescar() {
  const qc = useQueryClient()
  return () => {
    for (const k of ["finance-ruteo", "finance-puente", "finance-contraste",
                     "finance-pnl", "finance-kpis"]) {
      void qc.invalidateQueries({ queryKey: [k] })
    }
  }
}

/**
 * QUÉ VA A FINANZAS, Y DE DÓNDE SALE CADA MES.
 *
 * Cesar, 2026-10-01: «Tu hoja funciona bien como el primer data entry, pero en
 * verdad se debería de armar desde el maestro y debería de tener información
 * también del libro». El diseño está en `docs/finanzas-desde-el-maestro.md`, y
 * hasta hoy sólo se podía tocar por API.
 *
 * LAS CUATRO DECISIONES, EN EL ORDEN EN QUE SE TOMAN:
 *
 *   1. La FRONTERA — hasta qué mes manda la hoja. Es la grande, y la que mueve
 *      el P&L; por eso va arriba y exige motivo.
 *   2. El PUENTE — a qué cuenta del plan va cada línea de la hoja. Sin él, la
 *      línea se ACABA en la frontera y no sigue después.
 *   3. Las CUENTAS — si van a Finanzas, quién manda (libro o maestro) y en qué
 *      línea del P&L caen.
 *   4. Los DESCUADRES — lo que el libro y la hoja (o el libro y el maestro)
 *      dicen distinto. Cesar: «si no cuadran entre sí me avisas».
 */
export function QueVaAFinanzasPage() {
  const { activeTenantId } = useAuth()
  const { can } = useRbac()
  const { anio, pais, paisQuery } = useFinanceFilters()
  const puedeEditar = can("edit", activeTenantId ?? undefined)

  const ruteo = useQuery({
    queryKey: ["finance-ruteo", activeTenantId, pais],
    queryFn: () => fetchRuteo(paisQuery),
    enabled: Boolean(activeTenantId),
  })
  const pnl = useQuery({
    queryKey: ["finance-pnl", activeTenantId, anio, pais, "hoja"],
    queryFn: () => fetchPnl(anio, paisQuery, undefined, "hoja"),
    enabled: Boolean(activeTenantId),
  })
  const armado = pnl.data?.ensamblado ?? null

  return (
    <div className="space-y-4">
      <p className="flex items-start gap-1.5 rounded-md border border-dashed px-2.5 py-2 text-2xs leading-5 text-muted-foreground">
        <Info className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
        <span>
          {t("Finanzas tiene dos fuentes: tu hoja histórica y lo que el equipo mantiene en Marty (el libro y los maestros). Aquí eliges desde qué mes manda el maestro: antes de ese mes, Finanzas lee tu hoja; desde ese mes, lee el libro y los maestros, cuenta por cuenta. Si para un mismo mes el libro y el maestro no coinciden, te lo digo en vez de elegir por ti.")}
        </span>
      </p>

      <SeccionFrontera
        puedeEditar={puedeEditar}
        frontera={ruteo.data?.frontera ?? null}
        nota={ruteo.data?.nota}
        cargando={ruteo.isLoading}
      />

      {armado ? (
        <WorkPanel
          title={t("Lo que tengo que decirte antes de que mires el P&L")}
          meta={<span className="text-2xs text-muted-foreground">{t("Del P&L armado de")} {anio}</span>}
        >
          <div className="space-y-3 p-2.5">
            {armado.avisos.length > 0 ? (
              <ul className="space-y-1.5" role="status">
                {armado.avisos.map((a) => (
                  <li key={a} className="flex items-start gap-1.5 text-xs leading-5">
                    <TriangleAlert className="mt-1 h-3 w-3 shrink-0 text-warning" aria-hidden="true" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">{t("Nada que avisar: lo armado se sostiene.")}</p>
            )}

            {armado.en_la_frontera ? (
              <div>
                <p className="text-xs font-medium">
                  {t("En la frontera")} ({armado.en_la_frontera.meses.map((m) => MESES[m - 1]).join(", ")})
                  {" — "}{t("lo que dice tu hoja y lo que dirían tu libro y tu maestro del mismo mes")}
                </p>
                <Table className="mt-1">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Línea")}</TableHead>
                      <TableHead className="text-right">{t("Hoja")}</TableHead>
                      <TableHead className="text-right">{t("Libro + maestro")}</TableHead>
                      <TableHead className="text-right">{t("Cubre")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {armado.en_la_frontera.cuentas.map((c) => (
                      <TableRow key={c.account}>
                        <TableCell>{nombreCuenta(c.account)}</TableCell>
                        <TableCell className="num text-right">{formatAmount(c.hoja)}</TableCell>
                        <TableCell className="num text-right">{formatAmount(c.armado)}</TableCell>
                        <TableCell className={cn("num text-right",
                          c.cubre_pct !== null && Math.abs(c.cubre_pct - 100) > 10 && "font-medium text-warning")}>
                          {c.cubre_pct === null ? "—" : `${c.cubre_pct.toLocaleString("es")} %`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </div>
        </WorkPanel>
      ) : null}

      <SeccionPuente anio={anio} puedeEditar={puedeEditar} />

      <SeccionCuentas
        puedeEditar={puedeEditar}
        cuentas={ruteo.data?.detalle ?? []}
        lineas={ruteo.data?.lineas_del_modelo ?? []}
        cargando={ruteo.isLoading}
        error={ruteo.error}
      />

      <SeccionDescuadres anio={anio} pais={pais} paisQuery={paisQuery}
                         armado={armado} />
    </div>
  )
}

/* ------------------------------------------------------------ frontera --- */

function SeccionFrontera({
  puedeEditar, frontera, nota, cargando,
}: {
  puedeEditar: boolean
  frontera: { anio: number; mes: number; pais: string | null; motivo: string } | null
  nota?: string
  cargando: boolean
}) {
  const { toast } = useToast()
  const refrescar = useRefrescar()
  const hoy = new Date()
  const [anio, setAnio] = React.useState(frontera?.anio ?? hoy.getFullYear())
  const [mes, setMes] = React.useState(frontera?.mes ?? Math.max(1, hoy.getMonth()))
  const [alcance, setAlcance] = React.useState<string>("")
  const [motivo, setMotivo] = React.useState("")
  const [avisos, setAvisos] = React.useState<string[]>([])
  /*
    EL FORMULARIO ARRANCA EN LA FRONTERA VIGENTE. Se inicializaba al montar,
    antes de que llegara la respuesta, y enseñaba «septiembre» con la frontera
    en junio: quien pulsara «Declarar» la movía sin haberlo decidido.
  */
  React.useEffect(() => {
    if (!frontera) return
    setAnio(frontera.anio)
    setMes(frontera.mes)
    setAlcance(frontera.pais ?? "")
  }, [frontera?.anio, frontera?.mes, frontera?.pais])  // eslint-disable-line react-hooks/exhaustive-deps

  const declarar = useMutation({
    mutationFn: () => declararFrontera({ anio, mes, pais: alcance || null, motivo }),
    onSuccess: (r) => {
      setAvisos(r.avisos)
      setMotivo("")
      toast({ title: t("Fecha declarada: desde el mes siguiente manda el maestro"), description: r.siguiente })
      refrescar()
    },
    onError: onWriteError("la frontera", "declarar"),
  })
  const quitar = useMutation({
    mutationFn: () => quitarFrontera(motivo, frontera?.pais ?? null),
    onSuccess: (r) => {
      setAvisos([])
      setMotivo("")
      toast({ title: t("Fecha quitada: todo vuelve a salir de tu hoja"), description: r.siguiente })
      refrescar()
    },
    onError: onWriteError("la frontera", "quitar"),
  })
  const motivoValido = motivo.trim().length >= MOTIVO_MINIMO

  return (
    <WorkPanel
      title={t("Desde qué mes manda el maestro")}
      meta={
        <span className="text-2xs text-muted-foreground">
          {cargando ? "…" : frontera
            ? `${t("Hoy: tu hoja manda hasta")} ${MESES[frontera.mes - 1]} ${frontera.anio}; ${t("desde el mes siguiente manda el maestro")} · ${frontera.pais ? nombrePais(frontera.pais) : t("todas las sociedades")}`
            : t("Hoy: sin fecha declarada, todo Finanzas sale de tu hoja")}
        </span>
      }
      footnote={t("Es una fecha de corte de FUENTE, no de real contra proyección: un mes puede ya haber pasado y seguir saliendo de tu hoja, o salir del libro. Se puede mover o quitar, y cada cambio pide motivo porque mueve el P&L.")}
    >
      <div className="space-y-2.5 p-2.5 text-xs">
        {nota ? <p className="leading-5 text-muted-foreground">{nota}</p> : null}
        {frontera ? (
          <p className="leading-5">
            <span className="text-muted-foreground">{t("Motivo:")}</span> {frontera.motivo}
          </p>
        ) : null}

        {puedeEditar ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-2xs text-muted-foreground">{t("Último mes que sale de tu hoja")}</span>
              <span className="flex gap-1">
                <select aria-label={t("Mes")} value={mes} onChange={(e) => setMes(Number(e.target.value))}
                        className={selectClase}>
                  {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <Input aria-label={t("Año")} type="number" value={anio} min={2000} max={2100}
                       onChange={(e) => setAnio(Number(e.target.value))} className="h-7 w-20 text-xs" />
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-2xs text-muted-foreground">{t("Para")}</span>
              <select value={alcance} onChange={(e) => setAlcance(e.target.value)} className={selectClase}>
                <option value="">{t("todas las sociedades")}</option>
                {["PE", "MX", "BR", "PA"].map((p) => (
                  <option key={p} value={p}>{nombrePais(p)}</option>
                ))}
              </select>
            </label>
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1">
              <span className="text-2xs text-muted-foreground">
                {t("Motivo (obligatorio: mueve el P&L)")}
              </span>
              <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                     placeholder={t("p. ej. desde octubre el equipo carga el real en el maestro")}
                     className="h-7 text-xs" />
            </label>
            <Button size="sm" disabled={!motivoValido || declarar.isPending}
                    onClick={() => declarar.mutate()}>
              {t("Declarar la fecha")}
            </Button>
            {frontera ? (
              <Button size="sm" variant="outline" disabled={!motivoValido || quitar.isPending}
                      onClick={() => quitar.mutate()}>
                {t("Quitarla")}
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-2xs text-muted-foreground">{t("Tu rol puede mirar esto, no cambiarlo.")}</p>
        )}

        {avisos.length > 0 ? (
          <ul role="alert" className="space-y-1 rounded-md border border-warning/40 bg-warning-soft p-2">
            {avisos.map((a) => <li key={a} className="text-2xs leading-5">{a}</li>)}
          </ul>
        ) : null}
      </div>
    </WorkPanel>
  )
}

/* -------------------------------------------------------------- puente --- */

/**
 * EL PUENTE, LÍNEA A LÍNEA. Es trabajo de quien conoce el negocio: nadie más
 * sabe si «Freelancers» es costo de ventas o gasto. Por eso no se propone nada
 * por parecido de nombre — un puente adivinado es un puente convincente y falso.
 *
 * Las líneas sin puente van PRIMERO y por importe: son la lista de trabajo, y
 * mapear primero lo que más pesa es lo que antes acerca el P&L armado al tuyo.
 */
function SeccionPuente({ anio, puedeEditar }: { anio: number; puedeEditar: boolean }) {
  const { activeTenantId } = useAuth()
  const puente = useQuery({
    queryKey: ["finance-puente", activeTenantId, anio],
    queryFn: () => fetchPuente(anio),
    enabled: Boolean(activeTenantId),
  })
  const [verTodas, setVerTodas] = React.useState(false)
  const lineas = (puente.data?.lineas ?? []).filter((l) => l.importe !== 0)
  const visibles = verTodas ? lineas : lineas.filter((l) => !l.codigo_grupo)

  return (
    <WorkPanel
      title={t("El puente: tu hoja en el idioma de tu plan de cuentas")}
      meta={
        <span className="text-2xs text-muted-foreground">
          {puente.data ? `${puente.data.sin_puente} ${t("líneas sin puente")} · ${t("importes de")} ${anio}` : ""}
        </span>
      }
      actions={
        <Button size="sm" variant="outline" onClick={() => setVerTodas((v) => !v)}>
          {verTodas ? t("Sólo las que faltan") : t("Ver todas")}
        </Button>
      }
      footnote={t("Una línea sin puente se acaba en la frontera: el histórico sigue en tu hoja, y después no hay nada que la continúe. Si eliges una cuenta que cae en otra línea del P&L, el histórico se queda donde estaba y lo nuevo sale en la otra fila.")}
    >
      <DataState
        loading={puente.isLoading}
        error={puente.error}
        empty={visibles.length === 0}
        emptyMessage={verTodas ? t("Tu hoja no tiene líneas en este año.") : t("Todas las líneas de tu hoja tienen puente.")}
        onRetry={() => void puente.refetch()}
        rows={6}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Línea de tu hoja")}</TableHead>
              <TableHead className="text-right">{t("Importe")}</TableHead>
              <TableHead>{t("Va a la cuenta")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibles.map((l) => (
              <FilaPuente key={`${l.account}·${l.sub_account}`} linea={l}
                          cuentas={puente.data?.cuentas ?? []} puedeEditar={puedeEditar} />
            ))}
          </TableBody>
        </Table>
      </DataState>
    </WorkPanel>
  )
}

function FilaPuente({
  linea, cuentas, puedeEditar,
}: {
  linea: LineaDelPuente
  cuentas: Array<{ codigo: string; nombre: string; linea: string }>
  puedeEditar: boolean
}) {
  const refrescar = useRefrescar()
  const guardar = useMutation({
    mutationFn: async (codigo: string): Promise<unknown> => codigo
      ? tenderPuente({ account: linea.account, sub_account: linea.sub_account, codigo_grupo: codigo })
      : quitarPuente(linea.account, linea.sub_account),
    onSuccess: () => refrescar(),
    onError: onWriteError("el puente", "guardar"),
  })
  return (
    <TableRow>
      <TableCell>
        <span className="block text-xs">{linea.sub_account}</span>
        <span className="block text-2xs text-muted-foreground">{nombreCuenta(linea.account)}</span>
      </TableCell>
      <TableCell className="num text-right">{formatAmount(linea.importe)}</TableCell>
      <TableCell>
        <span className="flex flex-wrap items-center gap-1.5">
          <select
            aria-label={`${t("Cuenta para")} ${linea.sub_account}`}
            value={linea.codigo_grupo ?? ""}
            disabled={!puedeEditar || guardar.isPending}
            onChange={(e) => guardar.mutate(e.target.value)}
            className={cn(selectClase, "max-w-[22rem]")}
          >
            <option value="">{t("— sin puente —")}</option>
            {cuentas.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.codigo} · {c.nombre} ({nombreCuenta(c.linea)})
              </option>
            ))}
          </select>
          {linea.cambia_de_linea && linea.linea_destino ? (
            <Badge variant="warning-soft" title={t("El histórico se queda en su línea; lo de después sale en la otra.")}>
              <ArrowRightLeft className="mr-1 h-3 w-3" aria-hidden="true" />
              {t("pasa a")} {nombreCuenta(linea.linea_destino)}
            </Badge>
          ) : null}
        </span>
      </TableCell>
    </TableRow>
  )
}

/* ------------------------------------------------------------- cuentas --- */

/**
 * SÓLO LAS CUENTAS DE RESULTADO. Las de balance no tienen fila en el P&L, y
 * enseñar 166 cuentas para decidir sobre 43 esconde las que importan.
 */
function SeccionCuentas({
  puedeEditar, cuentas, lineas, cargando, error,
}: {
  puedeEditar: boolean
  cuentas: CuentaRuteo[]
  lineas: string[]
  cargando: boolean
  error: unknown
}) {
  const deResultado = cuentas.filter((c) => ["ingreso", "costo", "gasto"].includes(c.tipo_estado))
  const [abierta, setAbierta] = React.useState<string | null>(null)
  return (
    <WorkPanel
      title={t("Las cuentas: si van a Finanzas, y quién manda en cada una")}
      meta={<span className="text-2xs text-muted-foreground">{deResultado.length} {t("cuentas de resultado")}</span>}
      footnote={t("«Manda el libro»: lo asentado gana, y el maestro sólo cubre los meses que el libro todavía no asentó. «Manda el maestro»: al revés. Nunca se suman los dos — el mismo retainer está en el contrato y en la factura.")}
    >
      <DataState loading={cargando} error={error} empty={deResultado.length === 0}
                 emptyMessage={t("Este cliente no tiene plan de cuentas gerencial.")} rows={8}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Cuenta")}</TableHead>
              <TableHead>{t("Va a Finanzas")}</TableHead>
              <TableHead>{t("Manda")}</TableHead>
              <TableHead>{t("Línea del P&L")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {deResultado.map((c) => abierta === c.codigo ? (
              <EditorCuenta key={c.codigo} cuenta={c} lineas={lineas}
                            onCerrar={() => setAbierta(null)} />
            ) : (
              <TableRow key={c.codigo} className={cn(!c.va_a_finanzas && "text-muted-foreground")}>
                <TableCell>
                  <span className="num mr-1.5 text-2xs text-muted-foreground">{c.codigo}</span>
                  {c.nombre}
                </TableCell>
                <TableCell>{c.va_a_finanzas ? t("sí") : <Badge variant="outline">{t("no")}</Badge>}</TableCell>
                <TableCell>{c.fuente_finanzas === "libro" ? t("el libro") : t("el maestro")}</TableCell>
                <TableCell>
                  {c.linea_efectiva ? nombreCuenta(c.linea_efectiva) : <span className="text-muted-foreground">{t("fuera del P&L")}</span>}
                  {c.linea_del_modelo ? null : (
                    <span className="ml-1 text-2xs text-muted-foreground">{t("(por su código)")}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {puedeEditar ? (
                    <Button size="sm" variant="ghost" onClick={() => setAbierta(c.codigo)}>
                      {t("Cambiar")}
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataState>
    </WorkPanel>
  )
}

function EditorCuenta({
  cuenta, lineas, onCerrar,
}: {
  cuenta: CuentaRuteo
  lineas: string[]
  onCerrar: () => void
}) {
  const { toast } = useToast()
  const refrescar = useRefrescar()
  const [vaAFinanzas, setVaAFinanzas] = React.useState(cuenta.va_a_finanzas)
  const [fuente, setFuente] = React.useState<FuenteFinanzas>(cuenta.fuente_finanzas)
  const [linea, setLinea] = React.useState<string>(cuenta.linea_del_modelo ?? "")
  const [motivo, setMotivo] = React.useState("")
  const guardar = useMutation({
    mutationFn: () => enrutarCuenta({
      codigo: cuenta.codigo, va_a_finanzas: vaAFinanzas,
      va_a_contabilidad: cuenta.va_a_contabilidad,
      linea_del_modelo: linea || null, fuente_finanzas: fuente, motivo,
    }),
    onSuccess: (r) => {
      toast({ title: `${r.codigo} · ${r.nombre}`, description: r.aviso ?? t("Guardado. El P&L ya lo refleja.") })
      refrescar()
      onCerrar()
    },
    onError: onWriteError("la cuenta", "cambiar"),
  })
  return (
    <TableRow className="bg-surface-sunken/60">
      <TableCell>
        <span className="num mr-1.5 text-2xs text-muted-foreground">{cuenta.codigo}</span>
        {cuenta.nombre}
      </TableCell>
      <TableCell>
        <select aria-label={t("Va a Finanzas")} value={vaAFinanzas ? "si" : "no"}
                onChange={(e) => setVaAFinanzas(e.target.value === "si")} className={selectClase}>
          <option value="si">{t("sí")}</option>
          <option value="no">{t("no")}</option>
        </select>
      </TableCell>
      <TableCell>
        <select aria-label={t("Manda")} value={fuente}
                onChange={(e) => setFuente(e.target.value as FuenteFinanzas)} className={selectClase}>
          <option value="libro">{t("el libro")}</option>
          <option value="maestro">{t("el maestro")}</option>
        </select>
      </TableCell>
      <TableCell>
        <select aria-label={t("Línea del P&L")} value={linea}
                onChange={(e) => setLinea(e.target.value)} className={selectClase}>
          <option value="">{t("por su código")}</option>
          {lineas.map((l) => <option key={l} value={l}>{nombreCuenta(l)}</option>)}
        </select>
      </TableCell>
      <TableCell>
        <span className="flex items-center gap-1.5">
          <Input aria-label={t("Motivo")} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                 placeholder={t("Motivo")} className="h-7 w-48 text-xs" />
          <Button size="sm" disabled={motivo.trim().length < MOTIVO_MINIMO || guardar.isPending}
                  onClick={() => guardar.mutate()}>
            {t("Guardar")}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCerrar}>{t("Cancelar")}</Button>
        </span>
      </TableCell>
    </TableRow>
  )
}

/* ---------------------------------------------------------- descuadres --- */

/**
 * DOS COMPARACIONES, Y SE DICE CUÁL ES CUÁL.
 *
 * Con frontera: el libro contra el MAESTRO en los meses armados — es la que
 * decide qué se cuenta. Sin frontera (o antes de ella): el libro contra la
 * HOJA, que es la que dice si se puede declarar la frontera sin que el P&L
 * pegue un salto.
 */
function SeccionDescuadres({
  anio, pais, paisQuery, armado,
}: {
  anio: number
  pais: string | null
  paisQuery?: string
  armado: Ensamblado | null
}) {
  const { activeTenantId } = useAuth()
  const contraste = useQuery({
    queryKey: ["finance-contraste", activeTenantId, anio, pais],
    queryFn: () => fetchContraste(anio, paisQuery),
    enabled: Boolean(activeTenantId),
  })
  const descuadres = contraste.data?.descuadres ?? []

  return (
    <>
      {armado && armado.descuadres.length > 0 ? (
        <WorkPanel
          title={t("Libro contra maestro, en los meses armados")}
          meta={<span className="text-2xs text-muted-foreground">{armado.descuadres.length} {t("diferencias · manda el que dice la cuenta, y se avisa")}</span>}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Cuenta")}</TableHead>
                <TableHead>{t("Mes")}</TableHead>
                <TableHead className="text-right">{t("Libro")}</TableHead>
                <TableHead className="text-right">{t("Maestro")}</TableHead>
                <TableHead>{t("Se usó")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {armado.descuadres.map((d) => (
                <TableRow key={`${d.pais}${d.codigo}${d.mes}`}>
                  <TableCell>
                    <span className="num mr-1.5 text-2xs text-muted-foreground">{d.codigo}</span>
                    {d.cuenta}
                    <span className="ml-1.5 text-2xs text-muted-foreground">{nombrePais(d.pais)}</span>
                  </TableCell>
                  <TableCell>{MESES[d.mes - 1]}</TableCell>
                  <TableCell className="num text-right">{formatAmount(d.libro)}</TableCell>
                  <TableCell className="num text-right">{formatAmount(d.maestro)}</TableCell>
                  <TableCell>{d.usado === "libro" ? t("el libro") : t("el maestro")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorkPanel>
      ) : null}

      <WorkPanel
        title={t("Tu hoja contra el libro, cuenta por cuenta")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {contraste.data?.cuadra === null ? t("sin puente no hay qué comparar")
              : `${descuadres.length} ${t("descuadres")} · ${t("libro gerencial, en USD")}`}
          </span>
        }
        footnote={contraste.data?.nota}
      >
        <DataState
          loading={contraste.isLoading}
          error={contraste.error}
          empty={descuadres.length === 0}
          emptyMessage={contraste.data?.cuadra === null
            ? t("Todavía no hay puente entre tu hoja y tu plan: son dos vocabularios y no hay nada que comparar.")
            : t("Donde los dos tienen algo, cuadran.")}
          emptyHint={contraste.data?.cuadra === null
            ? t("Empieza por el puente, más arriba.")
            : t("Una cuenta que está sólo en uno de los dos no es un descuadre: es el calendario de carga.")}
          onRetry={() => void contraste.refetch()}
          rows={4}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Cuenta")}</TableHead>
                <TableHead>{t("Mes")}</TableHead>
                <TableHead className="text-right">{t("Hoja")}</TableHead>
                <TableHead className="text-right">{t("Libro")}</TableHead>
                <TableHead className="text-right">{t("Diferencia")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {descuadres.map((d) => (
                <TableRow key={`${d.codigo}${d.mes}`}>
                  <TableCell>
                    <span className="num mr-1.5 text-2xs text-muted-foreground">{d.codigo}</span>
                    {d.cuenta}
                  </TableCell>
                  <TableCell>{MESES[d.mes - 1]}</TableCell>
                  <TableCell className="num text-right">{formatAmount(d.hoja)}</TableCell>
                  <TableCell className="num text-right">{formatAmount(d.libro)}</TableCell>
                  <TableCell className="num text-right font-medium text-warning">{formatAmount(d.diferencia)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>
    </>
  )
}
